import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Package, Ruler } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

const PresetList = ({ title, icon: Icon, list, add, remove, testid, placeholder }) => {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => list().then((d) => setItems(d || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const onAdd = async () => {
    const v = name.trim();
    if (!v) return;
    setSaving(true);
    try { await add(v); setName(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
    finally { setSaving(false); }
  };

  const onRemove = async (id) => {
    if (!window.confirm("Remove this preset? It disappears from the field app immediately.")) return;
    try { await remove(id); load(); }
    catch { toast.error("Could not remove"); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6" data-testid={`preset-${testid}`}>
      <h3 className="mb-4 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest text-muted-foreground"><Icon className="w-4 h-4" /> {title}</h3>
      <div className="mb-4 flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onAdd()} placeholder={placeholder} data-testid={`preset-${testid}-input`} />
        <Button onClick={onAdd} disabled={saving} data-testid={`preset-${testid}-add`} className="rounded-full bg-primary hover:bg-primary/90 text-white shrink-0"><Plus className="w-4 h-4" /></Button>
      </div>
      {items.length ? (
        <ul className="divide-y divide-border border border-border rounded-lg overflow-hidden">
          {items.map((it) => (
            <li key={it.id} className="flex items-center justify-between px-3 py-2.5 text-sm" data-testid={`preset-${testid}-item-${it.id}`}>
              <span>{it.name}</span>
              <button onClick={() => onRemove(it.id)} data-testid={`preset-${testid}-remove-${it.id}`} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      ) : <p className="text-sm text-muted-foreground" data-testid={`preset-${testid}-empty`}>None yet. Add your first above.</p>}
    </div>
  );
};

export const PresetsTab = () => (
  <div className="fade-up">
    <div className="mb-6">
      <h2 className="font-display text-2xl font-bold">Presets</h2>
      <p className="text-sm text-muted-foreground">Products & lengths your crew picks from when logging removal line-items in the field app. Shared instantly.</p>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <PresetList title="Products" icon={Package} testid="products" placeholder="e.g. 92 stud 1.15"
        list={api.products} add={api.addProduct} remove={api.deleteProduct} />
      <PresetList title="Lengths" icon={Ruler} testid="lengths" placeholder="e.g. 2.4"
        list={api.lengths} add={api.addLength} remove={api.deleteLength} />
    </div>
  </div>
);
